import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || 'tiyu');
  const categoryArticles = getArticlesByCategory(currentCategory.slug);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 乐鱼官网`,
          description: currentCategory.description,
          url: `https://www.leyu.com/category/${currentCategory.slug}`,
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
            </div>
          </div>

          <section className="relative w-full h-[260px] md:h-[360px] overflow-hidden">
            <img
              src={currentCategory.image}
              alt={currentCategory.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-primary-500/20 backdrop-blur-sm mb-4">
                <i className={`${currentCategory.icon} text-3xl md:text-4xl text-primary-400`}></i>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-background-50/70 max-w-xl">
                {currentCategory.description}
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 flex-wrap mb-8 md:mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      cat.slug === currentCategory.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-100 rounded-2xl overflow-hidden transition-all hover:bg-background-200/70 cursor-pointer"
                    >
                      <div className="relative h-48 md:h-52 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {article.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {article.readTime}
                          </span>
                        </div>
                        <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                          {article.summary}
                        </p>
                        <div className="flex items-center flex-wrap gap-2 mt-4">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-700">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-base">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}